import type {RepositoryNWO} from '@github-ui/current-repository'
import {noop} from '@github-ui/noop'
import {commitDiscussionsPath} from '@github-ui/paths'
import {reactFetchJSON} from '@github-ui/verified-fetch'
import type React from 'react'
import {createContext, use, useCallback, useMemo, useState} from 'react'

import type {FetchRequestState} from '../shared/types'
import type {CommitComment} from '../types/commit-types'

type DiscussionCommentsData = {
  comments: CommitComment[]
  canLoadMore: boolean
  count: number
  subscribed: boolean
  providerState: FetchRequestState
}

type DiscussionCommentsActions = {
  addComment: (comments: CommitComment) => void
  deleteComment: (commentId: CommitComment['id']) => void
  updateComment: (comment: CommitComment) => void
  loadMore: () => void
  retry: () => void
}

// Split the dynamic comment data from the stable callback actions so a consumer
// reading only actions does not re-render when the comment data changes.
const DiscussionCommentsDataContext = createContext<DiscussionCommentsData>({
  comments: [],
  canLoadMore: false,
  count: 0,
  subscribed: false,
  providerState: 'initial',
})

const DiscussionCommentsActionsContext = createContext<DiscussionCommentsActions>({
  addComment: noop,
  deleteComment: noop,
  updateComment: noop,
  loadMore: noop,
  retry: noop,
})

export interface DiscussionCommentData {
  comments: CommitComment[]
  count: number
  canLoadMore: boolean
  subscribed: boolean
}

type DiscussionCommentsProviderProps = {
  comments?: CommitComment[]
  commentCount?: number
  canLoadMore?: boolean
  subscribed?: boolean
  providerState?: FetchRequestState
  repo: RepositoryNWO
  commitOid: string
}

const useInternalProviderState = ({
  externalComments = [],
  externalCommentCount = 0,
  externalCanLoadMore = false,
  externalSubscribed = false,
  externalProviderState = 'initial',
}: {
  externalComments?: CommitComment[]
  externalCommentCount?: number
  externalCanLoadMore?: boolean
  externalSubscribed?: boolean
  externalProviderState?: FetchRequestState
}) => {
  const [comments, setComments] = useState<CommitComment[]>(externalComments)
  const [count, setCount] = useState<number>(externalCommentCount)
  const [canLoadMore, setCanLoadMore] = useState(externalCanLoadMore)
  const [providerState, setProviderState] = useState(externalProviderState)
  const [subscribed, setSubscribed] = useState(externalSubscribed)

  const [previousExternalProviderState, setPreviousExternalProviderState] =
    useState<FetchRequestState>(externalProviderState)
  if (previousExternalProviderState !== externalProviderState) {
    setPreviousExternalProviderState(externalProviderState)
    setCount(externalCommentCount)
    setComments(externalComments)
    setCanLoadMore(externalCanLoadMore)
    setSubscribed(externalSubscribed)
    setProviderState(externalProviderState)
    return {
      comments: externalComments,
      setComments,
      count: externalCommentCount,
      setCount,
      canLoadMore: externalCanLoadMore,
      setCanLoadMore,
      providerState: externalProviderState,
      setProviderState,
      subscribed: externalSubscribed,
      setSubscribed,
    } as const
  }

  return {
    comments,
    setComments,
    count,
    setCount,
    canLoadMore,
    setCanLoadMore,
    providerState,
    setProviderState,
    subscribed,
    setSubscribed,
  } as const
}

export function DiscussionCommentsProvider({
  children,
  // eslint-disable-next-line @eslint-react/no-unstable-default-props
  comments: externalComments = [],
  commentCount: externalCommentCount = 0,
  canLoadMore: externalCanLoadMore = false,
  subscribed: externalSubscribed = false,
  providerState: externalProviderState = 'initial',
  repo,
  commitOid,
}: React.PropsWithChildren<DiscussionCommentsProviderProps>) {
  const {
    comments,
    setComments,
    count,
    setCount,
    canLoadMore,
    setCanLoadMore,
    providerState,
    setProviderState,
    subscribed,
    setSubscribed,
  } = useInternalProviderState({
    externalComments,
    externalCommentCount,
    externalCanLoadMore,
    externalSubscribed,
    externalProviderState,
  })

  const loadDiscussionComments = useCallback(
    async (beforeCommentId?: string) => {
      setProviderState('loading')

      const response = await reactFetchJSON(
        commitDiscussionsPath({
          owner: repo.ownerLogin,
          repo: repo.name,
          commitOid,
          beforeCommentId,
        }),
      )

      if (response.ok) {
        const data: DiscussionCommentData = await response.json()
        if (beforeCommentId) {
          setComments(c => [...data.comments, ...c]) // pagination
        } else {
          setComments(data.comments) //retry
        }

        setProviderState('loaded')

        // We should only need these value from the initial useFetchDeferredCommentData
        // however, in the case of a failure and retry, we need to update these values
        setCount(data.count)
        setSubscribed(data.subscribed)
        setCanLoadMore(data.canLoadMore)
      } else {
        setProviderState('error')
      }
    },
    [setProviderState, repo.ownerLogin, repo.name, commitOid, setCount, setSubscribed, setCanLoadMore, setComments],
  )

  const loadMore = useCallback(() => {
    const earliestComment = comments[0]

    if (!earliestComment || !canLoadMore) {
      return
    }

    loadDiscussionComments(earliestComment.id.toString())
  }, [canLoadMore, comments, loadDiscussionComments])

  const addComment = useCallback(
    (comment: CommitComment) => {
      setComments(p => [...p, comment])
      setCount(p => (p ?? 0) + 1)
    },
    [setComments, setCount],
  )

  const deleteComment = useCallback(
    (commentId: CommitComment['id']) => {
      setComments(p => p.filter(c => c.id !== commentId))
      setCount(p => (p ?? 0) - 1)
    },
    [setComments, setCount],
  )

  const updateComment = useCallback(
    (comment: CommitComment) => {
      setComments(p => p.map(c => (c.id === comment.id ? comment : c)))
    },
    [setComments],
  )

  const data = useMemo<DiscussionCommentsData>(
    () => ({comments, canLoadMore, count, subscribed, providerState}),
    [comments, canLoadMore, count, subscribed, providerState],
  )

  const actions = useMemo<DiscussionCommentsActions>(
    () => ({addComment, deleteComment, updateComment, loadMore, retry: loadDiscussionComments}),
    [addComment, deleteComment, updateComment, loadMore, loadDiscussionComments],
  )

  return (
    <DiscussionCommentsActionsContext value={actions}>
      <DiscussionCommentsDataContext value={data}>{children}</DiscussionCommentsDataContext>
    </DiscussionCommentsActionsContext>
  )
}

export function useDiscussionCommentsData() {
  return use(DiscussionCommentsDataContext)
}

export function useDiscussionCommentsActions() {
  return use(DiscussionCommentsActionsContext)
}
